import { IonContent } from '@ionic/react';
import React from 'react';

import { ReviewCardsCompatibility } from '@/components/ReviewCards';
import { UiTypography } from '@/components/ui/Typography';
import { useAccount } from '@/hooks/useAccount';
import { useCreateStripeClient } from '@/hooks/useCreateStripeClient';
import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign';
import { AnimationFill } from '@/pages/Onboarding/components/AnimationFill';
import { OnboardingFooter } from '@/pages/Onboarding/components/Footer';
import { OnboardingSubtitle } from '@/pages/Onboarding/components/Subtitle';
import { OwnAiTitle } from '@/pages/Onboarding/Layouts/OwnAI/components/Title';
import { userService } from '@/services/user';
import { trackOnboardingComplete } from '@/utils/analytics';
import { canLogEvents } from '@/utils/canLogEvents';

import { PageStyles } from './SettingUpProfileScreen.styles';

const ANIMATION_STEPS = [
  {
    delay: 0,
    label: 'Processing your request',
  },
  {
    delay: 4,
    label: 'Initializing AI modules',
  },
  {
    delay: 8,
    label: 'Adjusting your preferences',
  },
];

export const SettingUpProfileScreen: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'SettingUpProfile',
      });
    }
  }, []);

  const { refreshUser } = useAccount();
  const { createStripeClient, isCreatingStripeClient } =
    useCreateStripeClient();

  const [disabledNextStep, setDisabledNextStep] = React.useState<boolean>(true);

  const handleNextStep = async (): Promise<void> => {
    try {
      trackOnboardingComplete();

      const shouldCreateStripeClient = webCampaign.setup.paywall !== null;

      await Promise.all([
        shouldCreateStripeClient && createStripeClient(),
        userService.createOrUpdate({
          onboarding_data: {
            onboarded: true,
          },
        }),
      ]);

      refreshUser();
    } catch (error) {
      console.error('Error while finishing onboarding', error);
    }
  };

  const onFinishAnimation = () => {
    setDisabledNextStep(false);
  };

  return (
    <PageStyles background="dark-solid">
      <OwnAiTitle className="setting-up-title">
        Setting up your profile...
      </OwnAiTitle>
      <OnboardingSubtitle className="setting-up-subtitle">
        It will take less than one minute
      </OnboardingSubtitle>

      <IonContent>
        <AnimationFill
          onComplete={onFinishAnimation}
          show
          steps={ANIMATION_STEPS}
          stepDuration={4}
          titleToItemsSpacing={16}
          itemsSpacing={32}
          progressStyles={{
            backgroundColor: '#262130',
          }}
        />

        <section className="setting-up-user-reviews">
          <UiTypography variant="title-large" align="center" transform="bold">
            Trusted by 1,556,867 users
          </UiTypography>
          <ReviewCardsCompatibility />
        </section>
      </IonContent>

      <OnboardingFooter
        disabled={disabledNextStep || isCreatingStripeClient}
        onContinue={handleNextStep}
      />
    </PageStyles>
  );
};
